import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, CheckCircle2, ShieldCheck, ArrowRight, Check, PoundSterling, Play } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, resolveUrl } from "@/lib/apiClient";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const BUSINESS_TYPES = ["Hair Salon", "Nail Bar", "Barbershop", "Boutique Gym / CrossFit", "Day Spa", "Tanning Studio", "Wellness Hub", "Other"];

const embedUrl = (u) => {
  if (!u) return "";
  try {
    if (u.includes("youtube.com/watch")) { const id = new URL(u).searchParams.get("v"); return `https://www.youtube.com/embed/${id}`; }
    if (u.includes("youtu.be/")) { const id = u.split("youtu.be/")[1].split(/[?&]/)[0]; return `https://www.youtube.com/embed/${id}`; }
    if (u.includes("vimeo.com/") && !u.includes("player.vimeo")) { const id = u.split("vimeo.com/")[1].split(/[?&/]/)[0]; return `https://player.vimeo.com/video/${id}`; }
  } catch { /* noop */ }
  return u;
};

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6 } };
const money = (n) => `£${Number(n || 0).toLocaleString("en-GB")}`;

const Eyebrow = ({ children }) => (
  <p className="uppercase tracking-[0.22em] text-xs font-bold text-[var(--brand-secondary,#9A8C73)] mb-4">{children}</p>
);

export default function RecruitmentPage() {
  const { slug } = useParams();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [f, setF] = useState({ salon_name: "", contact_name: "", email: "", password: "", phone: "", city: "", business_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k, v) => setF({ ...f, [k]: v });

  useEffect(() => {
    api.get(`/public/business/${slug}`).then((r) => setBiz(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 6) return toast.error("Password must be at least 6 characters");
    setSubmitting(true);
    try { await api.post(`/public/business/${slug}/apply`, f); setDone(true); window.scrollTo({ top: document.getElementById("apply")?.offsetTop || 0, behavior: "smooth" }); }
    catch (err) { toast.error(apiErr(err)); } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] text-[#4A5550]">Loading…</div>;
  if (notFound || !biz) return <div className="min-h-screen flex flex-col items-center justify-center bg-[#FDFBF7] text-[#4A5550] gap-4"><p className="font-serif text-3xl">Recruitment page not found</p><Link to="/" className="underline">Back home</Link></div>;

  const primary = biz.primary_color || "#2C3531";
  const accent = biz.secondary_color || "#9A8C73";
  const brandVars = { "--brand-primary": primary, "--brand-secondary": accent };
  const field = "w-full bg-transparent border-b border-black/10 rounded-none px-0 py-3 text-[#1A201D] placeholder:text-[#9A8C73]/70 focus:border-[var(--brand-primary,#2C3531)] focus:outline-none transition-colors";
  const video = embedUrl(biz.video_url);
  const logoStyle = { height: `${biz.logo_height || 40}px`, width: biz.logo_width ? `${biz.logo_width}px` : "auto", maxHeight: "120px" };
  const treatments = biz.treatments || [];
  const steps = biz.steps || [];
  const caseStudies = biz.case_studies || [];
  const benefits = biz.benefits || [];
  const faqs = biz.faqs || [];

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain relative font-sans text-[#2C3531]" data-testid="recruitment-page" style={brandVars}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-6xl mx-auto px-6 md:px-10 min-h-[70px] py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {biz.logo_url ? <img src={resolveUrl(biz.logo_url)} alt={biz.name} className="object-contain" style={logoStyle} /> :
              <><Sparkles className="w-5 h-5" style={{ color: accent }} /><span className="font-serif text-2xl font-semibold" style={{ color: primary }}>{biz.name}</span></>}
          </div>
          <div className="flex items-center gap-2">
            <Link to="/salon/login" data-testid="header-salon-login" className="inline-flex items-center px-4 py-2.5 rounded-full border text-sm font-semibold hover:bg-black/5 transition-colors" style={{ borderColor: primary, color: primary }}>Salon login</Link>
            <a href="#apply" data-testid="header-apply-cta" className="hidden sm:inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white text-sm font-semibold hover:opacity-90 transition-opacity" style={{ backgroundColor: primary }}>Apply to host <ArrowRight className="w-4 h-4" /></a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-14 pb-20 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Eyebrow>Become a host partner</Eyebrow>
          <h1 className="font-serif text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.03] mb-6" style={{ color: primary }}>{biz.headline}</h1>
          <div className="rte-content text-[#4A5550] leading-relaxed max-w-xl text-lg" dangerouslySetInnerHTML={{ __html: biz.pitch || "" }} />
          <div className="flex flex-wrap gap-3 mt-8">
            <a href="#apply" data-testid="hero-apply-cta" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold hover:scale-[1.03] transition-transform duration-300" style={{ backgroundColor: primary }}>Apply to host <ArrowRight className="w-4 h-4" /></a>
            <a href="#treatments" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border font-semibold hover:bg-black/5 transition-colors" style={{ borderColor: primary, color: primary }}>View treatments & pricing</a>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="overflow-hidden rounded-[2.5rem] shadow-[0_20px_60px_rgba(0,0,0,0.15)]">
          {biz.hero_image && <img src={resolveUrl(biz.hero_image)} alt="" className="w-full h-[320px] lg:h-[500px] object-cover" />}
        </motion.div>
      </section>

      {/* Why partner */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-20 border-t border-black/5">
        <motion.div {...fadeUp} className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <Eyebrow>Why partner with us</Eyebrow>
            <h2 className="font-serif text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]" style={{ color: primary }}>{biz.intro_title}</h2>
          </div>
          <div className="lg:col-span-7">
            <div className="rte-content text-[#4A5550] text-lg leading-relaxed" dangerouslySetInnerHTML={{ __html: biz.intro_body || "" }} />
          </div>
        </motion.div>
      </section>

      {/* Treatments & pricing */}
      {treatments.length > 0 && (
        <section id="treatments" className="py-20" style={{ backgroundColor: "#F7F5F1" }}>
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <motion.div {...fadeUp} className="mb-10">
              <Eyebrow>Treatments & pricing</Eyebrow>
              <h2 className="font-serif text-4xl lg:text-5xl font-semibold tracking-tight" style={{ color: primary }}>What you'll be offering</h2>
              <p className="text-[#4A5550] mt-3 max-w-2xl text-lg">Every treatment carries a base price. You simply add your own markup on top and keep the difference on each booking.</p>
            </motion.div>
            <motion.div {...fadeUp} className="rounded-3xl border border-black/10 bg-white overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="hidden md:grid grid-cols-[1.6fr_0.8fr_0.9fr_0.9fr] px-6 py-3.5 text-[11px] uppercase tracking-wide font-bold text-[#787672] border-b border-black/5" style={{ backgroundColor: "#FDFBF7" }}>
                <span>Treatment</span><span className="text-right">Base price</span><span className="text-right">You add</span><span className="text-right">Client pays</span>
              </div>
              {treatments.map((t, i) => (
                <div key={i} data-testid={`treatment-row-${i}`} className="grid md:grid-cols-[1.6fr_0.8fr_0.9fr_0.9fr] gap-1 md:gap-0 px-6 py-4 border-b border-black/5 last:border-0 items-center">
                  <div>
                    <p className="font-serif text-lg font-bold" style={{ color: primary }}>{t.name}</p>
                    {t.description && <p className="text-sm text-[#787672] mt-0.5">{t.description}</p>}
                  </div>
                  <div className="md:text-right text-sm"><span className="md:hidden text-[#787672] mr-1">Base:</span>{money(t.base_price)}</div>
                  <div className="md:text-right"><span className="inline-flex items-center gap-1 text-sm font-bold px-2.5 py-1 rounded-full" style={{ color: primary, backgroundColor: accent + "33" }}>+{money(t.host_markup)}</span></div>
                  <div className="md:text-right font-serif text-lg font-bold" style={{ color: primary }}>{money((Number(t.base_price) || 0) + (Number(t.host_markup) || 0))}</div>
                </div>
              ))}
            </motion.div>
            <p className="text-xs text-[#9A8C73] mt-4 flex items-center gap-1.5"><PoundSterling className="w-3.5 h-3.5" /> Your markup is paid straight to you at checkout — example figures shown, set your own.</p>
          </div>
        </section>
      )}

      {/* How it works */}
      {(steps.length > 0 || video) && (
        <section id="how" className="max-w-6xl mx-auto px-6 md:px-10 py-24">
          <motion.div {...fadeUp} className="mb-12">
            <Eyebrow>How hosting works</Eyebrow>
            <h2 className="font-serif text-4xl lg:text-5xl font-semibold tracking-tight" style={{ color: primary }}>Up and earning in five simple steps</h2>
          </motion.div>
          {video && (
            <motion.div {...fadeUp} className="mb-14 rounded-3xl overflow-hidden border border-black/10 shadow-[0_12px_40px_rgba(0,0,0,0.10)] bg-black" data-testid="recruit-video">
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <iframe src={video} title="Intro video" className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </motion.div>
          )}
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
            {steps.map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.06 }} className="relative pl-24" data-testid={`step-${i}`}>
                <span className="absolute left-0 top-0 font-serif text-6xl font-bold leading-none select-none" style={{ color: primary, opacity: 0.14 }}>{s.num}</span>
                <h3 className="font-serif text-2xl font-bold mb-1.5" style={{ color: primary }}>{s.title}</h3>
                <p className="text-[#4A5550] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Case studies / income */}
      {caseStudies.length > 0 && (
        <section className="py-20" style={{ backgroundColor: primary }}>
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <motion.div {...fadeUp} className="mb-10 text-center">
              <p className="uppercase tracking-[0.22em] text-xs font-bold mb-4" style={{ color: accent }}>Real earning potential</p>
              <h2 className="font-serif text-4xl lg:text-5xl font-semibold tracking-tight text-[#FDFBF7]">What hosts like you can earn</h2>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-6">
              {caseStudies.map((c, i) => (
                <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }} className="rounded-3xl bg-[#FDFBF7] p-7 flex flex-col" data-testid={`case-study-${i}`}>
                  <h3 className="font-serif text-2xl font-bold mb-2" style={{ color: primary }}>{c.title}</h3>
                  <p className="text-[#4A5550] leading-relaxed flex-1">{c.scenario}</p>
                  <p className="mt-5 font-serif text-3xl font-bold" style={{ color: accent }}>{c.income}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      {benefits.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 md:px-10 py-24">
          <motion.div {...fadeUp} className="mb-12">
            <Eyebrow>Everything you need to know</Eyebrow>
            <h2 className="font-serif text-4xl lg:text-5xl font-semibold tracking-tight" style={{ color: primary }}>Zero risk, zero hassle</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.05 }} className="rounded-2xl border border-black/10 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`benefit-${i}`}>
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-4" style={{ backgroundColor: accent + "26", color: primary }}><Check className="w-5 h-5" /></span>
                <h3 className="font-serif text-xl font-bold mb-1.5" style={{ color: primary }}>{b.title}</h3>
                <p className="text-[#4A5550] text-sm leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-20 border-t border-black/5" style={{ backgroundColor: "#F7F5F1" }}>
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <motion.div {...fadeUp} className="mb-8">
              <Eyebrow>Questions</Eyebrow>
              <h2 className="font-serif text-4xl lg:text-5xl font-semibold tracking-tight" style={{ color: primary }}>Frequently asked</h2>
            </motion.div>
            <Accordion type="single" collapsible className="w-full" data-testid="recruit-faq">
              {faqs.map((q, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-black/10">
                  <AccordionTrigger className="text-left font-serif text-lg font-semibold hover:no-underline" style={{ color: primary }} data-testid={`faq-q-${i}`}>{q.q}</AccordionTrigger>
                  <AccordionContent className="text-[#4A5550] leading-relaxed text-base">{q.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Apply */}
      <section id="apply" className="py-24" style={{ backgroundColor: primary }}>
        <div className="max-w-2xl mx-auto px-6">
          {done ? (
            <div className="bg-[#FDFBF7] rounded-3xl p-12 text-center" data-testid="recruit-success">
              <CheckCircle2 className="w-14 h-14 mx-auto mb-5" style={{ color: accent }} />
              <h2 className="font-serif text-4xl font-semibold mb-3" style={{ color: primary }}>Application received</h2>
              <p className="text-[#4A5550] max-w-md mx-auto leading-relaxed">Thanks for applying to host {biz.name}. We'll review your details and email you at <strong>{f.email}</strong> once your co-branded booking portal is approved.</p>
            </div>
          ) : (
            <>
              <h2 className="font-serif text-4xl font-semibold text-[#FDFBF7] mb-2 text-center">Apply to host</h2>
              <p className="text-[#FDFBF7]/70 mb-8 text-center text-lg">Fill your empty chairs and earn on every treatment.</p>
              <form onSubmit={submit} data-testid="recruit-apply-form" className="bg-[#FDFBF7] rounded-3xl p-8 md:p-10 space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <input data-testid="apply-salon-name" className={field} placeholder="Salon / business name" value={f.salon_name} onChange={(e) => upd("salon_name", e.target.value)} required />
                  <input data-testid="apply-contact-name" className={field} placeholder="Your full name" value={f.contact_name} onChange={(e) => upd("contact_name", e.target.value)} required />
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <input data-testid="apply-email" type="email" className={field} placeholder="Login email" value={f.email} onChange={(e) => upd("email", e.target.value)} required />
                  <input data-testid="apply-password" type="password" className={field} placeholder="Create a password" value={f.password} onChange={(e) => upd("password", e.target.value)} required />
                </div>
                <p className="text-xs -mt-3" style={{ color: primary }} data-testid="apply-google-note">Tip: if you'd like to sign in with Google later, use your Google email address here.</p>
                <div className="grid sm:grid-cols-2 gap-6">
                  <input data-testid="apply-phone" className={field} placeholder="Phone number" value={f.phone} onChange={(e) => upd("phone", e.target.value)} />
                  <input data-testid="apply-city" className={field} placeholder="City / location" value={f.city} onChange={(e) => upd("city", e.target.value)} />
                </div>
                <Select value={f.business_type} onValueChange={(v) => upd("business_type", v)}>
                  <SelectTrigger data-testid="apply-business-type" className="border-0 border-b border-black/10 rounded-none px-0 shadow-none focus:ring-0"><SelectValue placeholder="Type of business" /></SelectTrigger>
                  <SelectContent>{BUSINESS_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
                <textarea data-testid="apply-message" className={`${field} resize-none`} rows={3} placeholder="Tell us about your salon (optional)" value={f.message} onChange={(e) => upd("message", e.target.value)} />
                <button type="submit" disabled={submitting} data-testid="apply-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-semibold hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60" style={{ backgroundColor: accent }}>
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit application <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="flex items-center justify-center gap-2 text-xs text-[#9A8C73]"><ShieldCheck className="w-4 h-4" /> Reviewed & approved before going live</p>
              </form>
              <p className="text-center text-sm text-[#FDFBF7]/70 mt-6">Already a host? <Link to="/salon/login" className="text-[#FDFBF7] font-semibold underline">Salon login</Link></p>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A201D] text-[#FDFBF7]" data-testid="recruit-footer">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
          <div className="grid md:grid-cols-[1.5fr_1fr] gap-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                {biz.logo_url ? <img src={resolveUrl(biz.logo_url)} alt={biz.name} className="h-8 object-contain" /> :
                  <span className="font-serif text-2xl font-semibold">{biz.name}</span>}
              </div>
              <p className="text-[#FDFBF7]/70 max-w-sm leading-relaxed">{biz.footer_note}</p>
            </div>
            <div className="md:text-right">
              <p className="uppercase tracking-[0.2em] text-xs font-bold mb-3" style={{ color: accent }}>Get in touch</p>
              {biz.contact_email && <a href={`mailto:${biz.contact_email}`} className="block text-[#FDFBF7]/80 hover:text-white transition-colors">{biz.contact_email}</a>}
              {biz.contact_phone && <a href={`tel:${biz.contact_phone}`} className="block text-[#FDFBF7]/80 hover:text-white transition-colors">{biz.contact_phone}</a>}
              <a href="#apply" className="inline-flex items-center gap-1.5 mt-4 px-5 py-2.5 rounded-full text-white text-sm font-semibold hover:opacity-90 transition-opacity" style={{ backgroundColor: accent }}>Apply to host <ArrowRight className="w-4 h-4" /></a>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs text-[#FDFBF7]/40">
            <span>© {new Date().getFullYear()} {biz.name}. All rights reserved.</span>
            <span>Powered by Grow Your Bookings</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
